import React from 'react';
import { motion } from 'framer-motion';

const FlightHUD = ({
  heading, altitude, speed, throttle,
  checkpointsTotal, checkpointsVisited,
  nextCheckpoint, compassBearing, distanceToNext,
  offCourse, onExit, pitch, bank, isMobile,
}) => {
  const hdgNum = Math.round(((360 - heading * 180 / Math.PI) % 360 + 360) % 360);
  const hdgStr = String(hdgNum).padStart(3, '0');
  const altStr = String(Math.round(1000 + altitude * 10)).padStart(4, '0');
  const tPct   = Math.round((throttle ?? 0.45) * 100);
  const progressPct = checkpointsTotal > 0 ? (checkpointsVisited / checkpointsTotal) * 100 : 0;

  return (
    <div className="flight-hud" aria-label="Flight HUD">

      {/* ── Top-left: instruments + throttle ── */}
      <div className="hud-panel hud-tl" role="status" aria-label="Flight instruments">
        <div className="hud-row">
          <span className="hud-label">HDG</span>
          <span className="hud-value">{hdgStr}<span className="hud-unit">°</span></span>
        </div>
        <div className="hud-row">
          <span className="hud-label">ALT</span>
          <span className="hud-value">{altStr}<span className="hud-unit">ft</span></span>
        </div>
        <div className="hud-row">
          <span className="hud-label">SPD</span>
          <span className="hud-value">{speed}<span className="hud-unit">kt</span></span>
        </div>

        {/* Throttle gauge */}
        <div className="hud-throttle-row">
          <span className="hud-label">THR</span>
          <div className="hud-throttle-bar" aria-label={`Throttle ${tPct}%`}>
            <motion.div
              className="hud-throttle-fill"
              animate={{ height: `${tPct}%` }}
              transition={{ duration: 0.12 }}
              style={{ background: tPct > 80 ? '#f2a65a' : tPct > 40 ? '#6fe3c4' : '#4a7a90' }}
            />
            <div className="hud-throttle-ticks">
              {[100,75,50,25,0].map(v => (
                <div key={v} className="hud-throttle-tick">
                  <span className="hud-throttle-tick-label">{v}</span>
                  <div className="hud-throttle-tick-line" />
                </div>
              ))}
            </div>
          </div>
          <span className="hud-value hud-throttle-pct"
            style={{ color: tPct > 80 ? '#f2a65a' : tPct > 40 ? '#6fe3c4' : '#4a7a90' }}>
            {tPct}<span className="hud-unit">%</span>
          </span>
        </div>

        {/* Progress */}
        <div className="hud-row hud-row--progress">
          <span className="hud-label">PROG</span>
          <span className="hud-value">{checkpointsVisited}/{checkpointsTotal}</span>
        </div>
        <div className="hud-progress-bar">
          <motion.div className="hud-progress-fill"
            animate={{ width: `${progressPct}%` }} transition={{ duration: 0.5 }} />
        </div>
      </div>

      {/* ── Top-right: compass ── */}
      <div className={`hud-panel hud-tr${offCourse ? ' hud-tr--warn' : ''}`} aria-label="Navigation">
        <div className="hud-nav-code">{nextCheckpoint ? nextCheckpoint.code : 'DONE'}</div>
        <div className="hud-nav-label">{nextCheckpoint ? nextCheckpoint.label : 'Mission Complete'}</div>
        <div className="hud-compass" aria-hidden="true">
          <div className="hud-compass-ring" />
          <div className="hud-compass-arrow"
            style={{ transform: `translate(-50%,-70%) rotate(${compassBearing}rad)` }} />
          <div className="hud-compass-center" />
        </div>
        <div className="hud-nav-dist">
          {distanceToNext !== null ? `${Math.round(distanceToNext)}m` : 'N/A'}
        </div>
        {offCourse && (
          <motion.div className="hud-warn-text"
            animate={{ opacity:[1,0.35,1] }} transition={{ duration:0.75, repeat:Infinity }}>
            OFF COURSE
          </motion.div>
        )}
      </div>

      {/* ── Bottom-left: waypoints (desktop) ── */}
      {!isMobile && (
        <div className="hud-panel hud-bl" aria-label="Flight plan">
          <div className="hud-minimap-label">FLIGHT PLAN</div>
          <div className="hud-waypoints" role="list">
            {Array.from({ length: checkpointsTotal }).map((_, i) => (
              <div key={i} role="listitem"
                className={`hud-waypoint${i < checkpointsVisited ? ' hud-waypoint--done' : ''}${i === checkpointsVisited ? ' hud-waypoint--current' : ''}`} />
            ))}
          </div>
        </div>
      )}

      {/* ── Bottom-right: attitude (desktop) ── */}
      {!isMobile && (
        <div className="hud-attitude-wrap" aria-hidden="true">
          <div className="hud-attitude">
            <div className="hud-ai-horizon"
              style={{ transform: `rotate(${-bank*180/Math.PI}deg) translateY(${pitch*90}px)` }} />
            <div className="hud-ai-mark" />
          </div>
          <div className="hud-attitude-label">ATT</div>
        </div>
      )}

      {/* ── Exit ── */}
      <motion.button className="hud-exit-btn" onClick={onExit}
        whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }} aria-label="Exit Flight Mode">
        <ExitIcon /><span>Exit</span>
      </motion.button>
    </div>
  );
};

const ExitIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
    <path d="M9 2.5L13.5 7.5L9 12.5M13.5 7.5H4.5M2 2V13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default FlightHUD;